import React, { useState, useEffect, useRef, useMemo } from "react";
import { Button, Spinner, TypographyP } from "@finster/ui";
import { Menu, ChevronDown, Check } from "lucide-react";
import { SlideQAResult, SlideIssue } from "../services/ai";
import { ReviewProgress } from "../hooks/useDeckReview";

const ISSUE_TYPE_COLORS: Record<string, { bg: string; text: string }> = {
  typo: { bg: "bg-red-100", text: "text-red-600" },
  grammar: { bg: "bg-yellow-100", text: "text-yellow-700" },
  factual: { bg: "bg-red-100", text: "text-red-600" },
  design: { bg: "bg-amber-100", text: "text-amber-700" },
  clarity: { bg: "bg-rose-100", text: "text-rose-600" },
  consistency: { bg: "bg-blue-100", text: "text-blue-600" },
};

interface FlatIssue {
  slideIndex: number;
  issue: SlideIssue;
}

interface DeckReviewProps {
  onFixIssue: (query: string) => void;
  onOpenSidebar: () => void;
  reviewResults: SlideQAResult[];
  isReviewing: boolean;
  reviewProgress: ReviewProgress | null;
  slideCount: number;
  selectedSlides: number[];
  setSelectedSlides: (slides: number[] | ((prev: number[]) => number[])) => void;
  openSlideSelector: () => void;
  startReview: (slides: number[]) => void;
  clearResults: () => void;
  goToSlide: (index: number) => Promise<void>;
}

const DeckReview: React.FC<DeckReviewProps> = ({
  onFixIssue,
  onOpenSidebar,
  reviewResults,
  isReviewing,
  reviewProgress,
  slideCount,
  selectedSlides,
  setSelectedSlides,
  openSlideSelector,
  startReview,
  clearResults,
  goToSlide,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const [expandedCard, setExpandedCard] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hasInitialised = useRef(false);

  const flatIssues = useMemo<FlatIssue[]>(() => {
    const items: FlatIssue[] = [];
    for (const result of reviewResults) {
      for (const issue of result.issues || []) {
        items.push({ slideIndex: result.slideIndex, issue });
      }
    }
    return items;
  }, [reviewResults]);

  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const { issue } of flatIssues) {
      counts[issue.type] = (counts[issue.type] || 0) + 1;
    }
    return counts;
  }, [flatIssues]);

  const filteredIssues =
    activeFilter === "all"
      ? flatIssues.map((f, i) => ({ ...f, originalIndex: i }))
      : flatIssues
          .map((f, i) => ({ ...f, originalIndex: i }))
          .filter((f) => f.issue.type === activeFilter);

  useEffect(() => {
    if (!hasInitialised.current && slideCount === 0 && !isReviewing && reviewResults.length === 0) {
      hasInitialised.current = true;
      openSlideSelector();
    }
  }, [slideCount, isReviewing, reviewResults.length, openSlideSelector]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const allSelected = slideCount > 0 && selectedSlides.length === slideCount;

  const dropdownLabel = allSelected
    ? "All Slides"
    : selectedSlides.length === 0
      ? "Select slides..."
      : `${selectedSlides.length} slide${selectedSlides.length !== 1 ? "s" : ""} selected`;

  const buttonLabel = allSelected
    ? "Review All Slides"
    : `Review ${selectedSlides.length} Slide${selectedSlides.length !== 1 ? "s" : ""}`;

  const toggleAllSlides = () => {
    if (allSelected) {
      setSelectedSlides([]);
    } else {
      setSelectedSlides(Array.from({ length: slideCount }, (_, i) => i));
    }
  };

  const toggleSlide = (index: number) => {
    if (selectedSlides.includes(index)) {
      setSelectedSlides((prev: number[]) => prev.filter((s) => s !== index));
    } else {
      setSelectedSlides((prev: number[]) => [...prev, index].sort((a, b) => a - b));
    }
  };

  const handleReviewAgain = () => {
    setActiveFilter("all");
    setExpandedCard(null);
    clearResults();
  };

  const showInitial = !isReviewing && reviewResults.length === 0;
  const showResults = reviewResults.length > 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-none px-4 pt-3 pb-2 flex items-center justify-between">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onOpenSidebar}
            aria-label="Open sidebar"
            className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-muted-foreground hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus focus-visible:ring-offset-2"
          >
            <Menu className="h-5 w-5" aria-hidden />
          </button>
          <TypographyP>Review Mode</TypographyP>
        </div>
      </div>

      {/* Initial / slide selection view */}
      {showInitial && (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 pt-4 pb-4">
          <h2 className="text-2xl font-bold text-foreground mb-2">Review presentation</h2>
          <p className="text-sm text-muted-foreground leading-relaxed mb-6">
            Analyse your deck for grammatical errors, inconsistencies across slides or factual
            errors within seconds.
          </p>

          {slideCount > 0 && (
            <>
              <p className="text-sm text-muted-foreground mb-2">Select slides for review</p>

              <div className="relative mb-4" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={() => setDropdownOpen((o) => !o)}
                  className="flex w-full cursor-pointer items-center justify-between rounded-lg border border-border bg-background px-4 py-3 text-sm text-foreground hover:border-primary/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus"
                >
                  <span>{dropdownLabel}</span>
                  <ChevronDown
                    className={`h-4 w-4 text-muted-foreground transition-transform ${dropdownOpen ? "rotate-180" : ""}`}
                  />
                </button>

                {dropdownOpen && (
                  <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-64 overflow-y-auto rounded-lg border border-border bg-background shadow-lg">
                    <button
                      type="button"
                      onClick={toggleAllSlides}
                      className="flex w-full cursor-pointer items-center gap-3 border-b border-border px-4 py-3 text-sm text-foreground hover:bg-accent"
                    >
                      <span
                        className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded ${allSelected ? "bg-primary text-white" : "border border-border"}`}
                      >
                        {allSelected && <Check className="h-3.5 w-3.5" />}
                      </span>
                      <span>All Slides</span>
                    </button>

                    {Array.from({ length: slideCount }, (_, i) => {
                      const checked = selectedSlides.includes(i);
                      return (
                        <button
                          type="button"
                          key={i}
                          onClick={() => toggleSlide(i)}
                          className="flex w-full cursor-pointer items-center gap-3 border-b border-border px-4 py-3 text-sm text-foreground last:border-b-0 hover:bg-accent"
                        >
                          <span
                            className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded ${checked ? "bg-primary text-white" : "border border-border"}`}
                          >
                            {checked && <Check className="h-3.5 w-3.5" />}
                          </span>
                          <span>Slide {i + 1}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <Button
                type="button"
                disabled={selectedSlides.length === 0}
                onClick={() => startReview(selectedSlides)}
                className="w-full cursor-pointer rounded-xl py-3 text-sm font-semibold"
              >
                {buttonLabel}
              </Button>
            </>
          )}

          {slideCount === 0 && (
            <div className="flex flex-1 items-center justify-center">
              <Spinner size="sm" />
            </div>
          )}
        </div>
      )}

      {/* Progress bar while reviewing */}
      {isReviewing && reviewProgress && (
        <div className="flex-none px-4 py-3">
          <div className="flex items-center gap-2.5 mb-2">
            <Spinner size="sm" />
            <span className="text-xs font-medium text-foreground">
              {reviewProgress.phase === "export"
                ? `Exporting slide ${reviewProgress.current} of ${reviewProgress.total}...`
                : `Reviewing slide ${reviewProgress.current} of ${reviewProgress.total}...`}
            </span>
          </div>
          <div className="h-1 w-full overflow-hidden rounded-full bg-border">
            <div
              className="h-full rounded-full transition-[width] duration-300"
              style={{
                width: `${(reviewProgress.current / reviewProgress.total) * 100}%`,
                backgroundColor:
                  reviewProgress.phase === "export"
                    ? "hsl(var(--primary-hover))"
                    : "hsl(var(--primary))",
              }}
            />
          </div>
        </div>
      )}

      {/* Results view — mirrors ChatView's main structure */}
      {showResults && (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 pt-2 pb-3">
          {/* Filter tabs */}
          <div className="flex-none overflow-x-auto py-1 pb-3">
            <div className="flex gap-2 px-0.5">
              <button
                type="button"
                onClick={() => setActiveFilter("all")}
                className={`shrink-0 cursor-pointer rounded-full border-0 px-3 py-1 text-[11px] font-medium transition-colors ${
                  activeFilter === "all"
                    ? "bg-primary text-white"
                    : "bg-accent text-foreground hover:bg-accent/80"
                }`}
              >
                All ({flatIssues.length})
              </button>
              {Object.entries(typeCounts).map(([type, count]) => (
                  <button
                    type="button"
                    key={type}
                    onClick={() => setActiveFilter(type)}
                    className={`shrink-0 cursor-pointer rounded-full border px-3 py-1 text-[11px] font-medium capitalize transition-colors ${
                      activeFilter === type
                        ? "border-foreground bg-foreground text-white"
                        : "border-border bg-background text-foreground hover:bg-accent"
                    }`}
                  >
                    {type} ({count})
                  </button>
              ))}
            </div>
          </div>

          {/* Scrollable issue cards */}
          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden mx-auto w-full" style={{ maxWidth: "100%" }}>
            {filteredIssues.map(({ slideIndex, issue, originalIndex }) => {
              const colors = ISSUE_TYPE_COLORS[issue.type] || ISSUE_TYPE_COLORS.consistency;
              const cardKey = `${slideIndex}-${originalIndex}`;
              const isExpanded = expandedCard === cardKey;
              return (
                <div
                  key={cardKey}
                  role="button"
                  tabIndex={0}
                  onClick={() => setExpandedCard(isExpanded ? null : cardKey)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setExpandedCard(isExpanded ? null : cardKey);
                    }
                  }}
                  className={`mb-2 cursor-pointer overflow-hidden break-words rounded-xl border border-border p-2.5 transition-colors hover:bg-gray-50 ${isExpanded ? "bg-gray-50" : "bg-background"}`}
                >
                  <div className="mb-1 flex items-center gap-1.5">
                    <span className="text-[10px] font-medium text-muted-foreground">
                      Slide {slideIndex + 1}
                    </span>
                    <span
                      className={`rounded px-1.5 py-px text-[9px] font-semibold capitalize ${colors.bg} ${colors.text}`}
                    >
                      {issue.type}
                    </span>
                  </div>
                  <p className="m-0 break-words text-[11px] leading-snug text-foreground">
                    {issue.description}
                  </p>
                  {isExpanded && issue.suggestion && (
                    <div className="mt-1.5 overflow-hidden break-words rounded-md border border-dashed border-primary/40 bg-primary/5 px-2 py-1.5">
                      <p className="m-0 break-words text-[10px] leading-snug text-foreground">
                        {issue.suggestion}
                      </p>
                      <div className="mt-1 text-right">
                        <button
                          type="button"
                          onClick={async (e) => {
                            e.stopPropagation();
                            try { await goToSlide(slideIndex); } catch {}
                            onFixIssue(`${issue.description}. Please fix this: ${issue.suggestion}`);
                          }}
                          className="cursor-pointer rounded bg-primary px-2 py-0.5 text-[10px] font-semibold text-white border-0 hover:bg-primary/90"
                        >
                          Fix
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {!isReviewing && flatIssues.length === 0 && (
              <div className="rounded-xl border-2 border-primary bg-primary/5 p-6 text-center">
                <span className="text-3xl">🎉</span>
                <p className="mt-2 text-sm font-semibold text-primary">
                  Perfect! No issues found.
                </p>
              </div>
            )}
          </div>

          {/* Fixed bottom bar for Review Again */}
          {!isReviewing && (
            <div className="flex-none border-t border-border px-4 py-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleReviewAgain}
                className="w-full cursor-pointer"
              >
                Review Again
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DeckReview;
